import React from "react";
import {
  MousePointer2,
  Type,
  CheckSquare,
  PenLine,
  ScanSearch,
  Sparkles,
  PanelLeft,
  Loader2,
} from "lucide-react";

/**
 * Floating toolbar centered over the PDF canvas (Figma/Acrobat pattern).
 * Merges the old left rail + "Add Field" dropdown into one place:
 *   Select · Add Text · Add Check  |  Detect fields · Generate answers  |  Fields
 */
const ToolButton = ({ icon: Icon, label, active, onClick, disabled }) => (
  <button
    type="button"
    title={label}
    aria-label={label}
    aria-pressed={active}
    disabled={disabled}
    onClick={onClick}
    className={`flex flex-col items-center justify-center w-[52px] h-[44px] rounded-lg text-[10px] font-medium gap-0.5 transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
      active
        ? "bg-[#C95EBE] text-white"
        : "text-[#52525B] hover:bg-gray-100"
    }`}
  >
    <Icon size={16} />
    {label}
  </button>
);

const FormCanvasToolbar = ({
  activeTool, // 'select' | 'text' | 'check' | 'sign'
  onSelect,
  onAddText,
  onAddCheck,
  onSign,
  onDetect,
  detecting,
  onGenerate,
  generating,
  canGenerate,
  aiComplete,
  onToggleFields,
  fieldsCollapsed,
  disabled,
}) => {
  const aiBusy = detecting || generating;
  // Hide the AI action once answers exist — but keep it visible while running.
  const showAi = !aiComplete || aiBusy;
  return (
    <div className="absolute left-1/2 -translate-x-1/2 bottom-5 z-20 flex items-center gap-1 bg-white/95 backdrop-blur rounded-2xl shadow-xl border border-gray-200 px-2 py-1.5">
      <ToolButton
        icon={MousePointer2}
        label="Select"
        active={activeTool === "select"}
        onClick={onSelect}
      />
      <ToolButton
        icon={Type}
        label="Text"
        active={activeTool === "text"}
        onClick={onAddText}
        disabled={disabled}
      />
      <ToolButton
        icon={CheckSquare}
        label="Check"
        active={activeTool === "check"}
        onClick={onAddCheck}
        disabled={disabled}
      />
      <ToolButton
        icon={PenLine}
        label="Sign"
        active={activeTool === "sign"}
        onClick={onSign}
        disabled={disabled}
      />

      {/* One AI button: "Detect fields" first, then "Generate answers" once
          fields exist — and it disappears once answers are generated. */}
      {showAi && (
        <>
          <div className="w-px h-7 bg-gray-200 mx-1" />
          {canGenerate ? (
            <button
              type="button"
              title="Draft answers for the current page with AI — you review before saving"
              aria-label="Generate answers for the current page with AI"
              disabled={aiBusy || disabled}
              onClick={onGenerate}
              className="flex items-center gap-1.5 h-[44px] px-3 rounded-lg text-[13px] font-medium bg-[#FDF2FB] text-[#C95EBE] hover:bg-[#FAE5F4] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {generating ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <Sparkles size={16} />
              )}
              {generating ? "Generating…" : "Generate answers"}
            </button>
          ) : (
            <button
              type="button"
              title="Let AI find the form fields"
              aria-label="Detect fields with AI"
              disabled={aiBusy || disabled}
              onClick={onDetect}
              className="flex items-center gap-1.5 h-[44px] px-3 rounded-lg text-[13px] font-medium bg-[#FDF2FB] text-[#C95EBE] hover:bg-[#FAE5F4] transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {detecting ? (
                <Loader2 size={16} className="animate-spin" />
              ) : (
                <ScanSearch size={16} />
              )}
              {detecting ? "Detecting…" : "Detect fields"}
            </button>
          )}
        </>
      )}

      <div className="w-px h-7 bg-gray-200 mx-1" />

      <ToolButton
        icon={PanelLeft}
        label="Fields"
        active={!fieldsCollapsed}
        onClick={onToggleFields}
      />
    </div>
  );
};

export default FormCanvasToolbar;
